import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { graphql } from 'babel-plugin-relay/macro'

import styled from 'styled-components'
import { InputBase, Link, Paper, Tab, Tabs, Typography } from '@material-ui/core'
import { Skeleton, Pagination } from '@material-ui/lab'
import DescriptionIcon from '@material-ui/icons/Description'
import SearchIcon from '@material-ui/icons/Search'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { InvoiceRunsQuery } from '../../__generated__/InvoiceRunsQuery.graphql'
import { useLocation } from 'react-router'
import { parse } from 'qs'
import { debounce } from 'lodash'
import useWorkspaceNavigate from '../../hooks/useWorkspaceNavigate'
import StatusChip, { ChipTone } from '../../components/StatusChip'
import { Clamped, CopyableText, FullText, useCopy } from '../../components/ClampedText'

const RootPaper = styled(Paper)``
const TitleContainer = styled.div`
  padding: 16px;
`
const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px;
`
const TableWrapper = styled.div`
  overflow-x: auto;
`
// Fixed layout so the columns divide the width that is there rather than the width the
// longest file name would like: the percentages below always add up to the table, so the
// page never scrolls sideways however long a subject or a container number runs.
const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`
const Col = styled.col<{ $width: string }>`
  width: ${(props) => props.$width};
`
const Th = styled.th`
  text-align: left;
  padding: 12px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Td = styled.td`
  padding: 12px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  vertical-align: middle;
  /* VINs and container numbers have no spaces to break at, and a fixed layout would let
     them run under the next column rather than widening the one they are in. */
  overflow-wrap: anywhere;
`
const Muted = styled.span`
  color: rgba(0, 0, 0, 0.5);
`
// Unresolved entities read as muted text rather than a link, but are still worth copying:
// that is the number to search on when working out why nothing was matched.
const MutedText = styled(CopyableText)`
  color: rgba(0, 0, 0, 0.5);
`
const ErrorText = styled(CopyableText)`
  color: #b00020;
  font-size: 0.8rem;
  margin-top: 2px;
`
const FileLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;

  & > svg {
    flex: none;
  }
`
const EntityLink = styled(Link)`
  display: inline-block;
  max-width: 100%;
`
// One cargo line per row rather than a comma-run: a bill of lading routinely carries
// several, and 17-character VINs run together into an unreadable block in a column this
// narrow. Stretched rather than flex-start so each child fills the column and can clamp -
// a shrink-to-fit child has nothing to ellipsis against and overflows instead.
const CargoList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  min-width: 0;
`
const CargoLink = styled(Link)`
  display: block;
  min-width: 0;
`
const SubText = styled.div`
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.8rem;
  margin-top: 2px;
`
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px;
`
const TitleBlock = styled.div`
  min-width: 0;
`
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  width: 320px;
  max-width: 100%;
  max-height: 44px;
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  background: ${(props) => props.theme.palette.background.paper};
`
const SearchInput = styled(InputBase)`
  flex: 1;
  & > input {
    padding: 10px 4px;
    text-indent: 8px;
  }
`
const SearchIconBox = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px;
  color: ${(props) => props.theme.palette.text.secondary};
  border-left: 1px solid ${(props) => props.theme.palette.divider};
`
const STATUS_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Processed', value: 'processed' },
  { label: 'Failed', value: 'failed' },
  { label: 'Value exists', value: 'skipped_value_exists' },
  { label: 'Duplicate', value: 'skipped_duplicate_invoice' },
  { label: 'Skipped', value: 'skipped_by_triage' },
]

const STATUS_LABELS: Record<string, string> = {
  skipped_by_triage: 'skipped',
  // The container already had a figure in that cost field, so nothing was written.
  // Not a failure: the invoice is filed and waiting on a human to reconcile the two.
  skipped_value_exists: 'value exists',
  // A cumulative cost (transport) whose invoice number was already booked onto that
  // container. Not added a second time; the PDF is filed all the same.
  skipped_duplicate_invoice: 'duplicate',
}

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status

const statusTone = (status: string): ChipTone => {
  if (status === 'processed') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'skipped_value_exists') return 'warning'
  if (status === 'skipped_duplicate_invoice') return 'warning'
  if (status === 'skipped_by_triage') return 'info'
  return 'neutral'
}

// ISO 6346 comparison form, the same normalisation the backend resolver matches on.
const normalize = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '')

// Odyssey stores composite container numbers ("FFAU4985016_EXP-SANTOVA-SHAMS2608025-1-NEU")
// and an invoice prints only the box number, so the label normally contains the reference.
// When it does not, the charge landed on a box the invoice never named - worth showing.
const referenceMismatch = (label: string | null, reference: string | null): boolean =>
  !!label && !!reference && !normalize(label).includes(normalize(reference))

const confidenceTone = (confidence: string | null): ChipTone => {
  if (confidence === 'high') return 'success'
  if (confidence === 'medium') return 'warning'
  if (confidence === 'low') return 'danger'
  return 'neutral'
}

const InvoiceRuns: React.FC = () => {
  const location = useLocation()
  const wsNavigate = useWorkspaceNavigate()
  const queryVariables = parse(location.search, { ignoreQueryPrefix: true })
  const page = Number.parseInt(queryVariables.page as string) || 1
  const perPage = Number.parseInt(queryVariables.perPage as string) || 20
  const status = (queryVariables.status as string) || 'all'
  const search = (queryVariables.search as string) || ''

  // Typing updates the field immediately but the URL (and so the query) only after a
  // pause - one request per search, not one per keystroke.
  const [searchInput, setSearchInput] = useState(search)
  const pushSearch = useMemo(
    () => debounce((value: string) => wsNavigate.relative({ status, search: value || undefined, page: 1 }), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status]
  )
  useEffect(() => pushSearch.cancel, [pushSearch])
  // Keeps the field honest when the URL changes from elsewhere, e.g. the back button.
  useEffect(() => setSearchInput(search), [search])

  return (
    <RootPaper variant='outlined'>
      <HeaderBar>
        <TitleBlock>
          <Typography variant='h5'>AI Processing</Typography>
          <Typography variant='body1'>Documents read from email by AI and booked onto their respective entities</Typography>
        </TitleBlock>
        <SearchBox>
          <SearchInput
            fullWidth
            placeholder='Search entity, e.g. MEDU4637582'
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
              pushSearch(e.target.value)
            }}
          />
          <SearchIconBox>
            <SearchIcon fontSize='small' />
          </SearchIconBox>
        </SearchBox>
      </HeaderBar>
      <Tabs
        value={status}
        indicatorColor='primary'
        textColor='primary'
        variant='scrollable'
        scrollButtons='auto'
        onChange={(_, newStatus) => wsNavigate.relative({ status: newStatus, search: search || undefined, page: 1 })}
      >
        {STATUS_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
      <Suspense fallback={<InvoiceRunsTable.Skeleton />}>
        <InvoiceRunsTable
          page={page}
          perPage={perPage}
          status={status}
          search={search}
          onPageChange={(newPage) => wsNavigate.relative({ status, search: search || undefined, page: newPage })}
        />
      </Suspense>
    </RootPaper>
  )
}

interface Params {
  page: number
  perPage: number
  status: string
  search: string
  onPageChange: (page: number) => void
}

const InvoiceRunsTable: React.FC<Params> & { Skeleton: React.FC } = ({ page, perPage, status, search, onPageChange }) => {
  const copy = useCopy()
  const data = useLazyLoadQuery<InvoiceRunsQuery>(query, {
    page,
    perPage,
    status,
    searchQuery: search || null,
  })
  const runs = data.invoiceRunList.invoiceRuns ?? []

  if (runs.length === 0) {
    return (
      <TitleContainer>
        <Muted>{search ? `No documents found for "${search}".` : 'No documents processed yet.'}</Muted>
      </TitleContainer>
    )
  }

  return (
    <>
      <TableWrapper>
        <Table>
          <colgroup>
            <Col $width='13%' />
            <Col $width='7%' />
            <Col $width='15%' />
            <Col $width='16%' />
            <Col $width='15%' />
            <Col $width='9%' />
            <Col $width='16%' />
            <Col $width='9%' />
          </colgroup>
          <thead>
            <tr>
              <Th>File</Th>
              <Th>Source</Th>
              <Th>Subject</Th>
              <Th>Entity</Th>
              <Th>Extracted</Th>
              <Th>Confidence</Th>
              <Th>Status</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.slug}>
                <Td>
                  {run.fileName ? (
                    <FullText text={run.fileName}>
                      {run.fileUrl ? (
                        <FileLink href={run.fileUrl} target='_blank' rel='noopener noreferrer'>
                          <DescriptionIcon fontSize='small' />
                          <Clamped>{run.fileName}</Clamped>
                        </FileLink>
                      ) : (
                        <Clamped>{run.fileName}</Clamped>
                      )}
                    </FullText>
                  ) : (
                    <Muted>--</Muted>
                  )}
                </Td>
                <Td>{run.source}</Td>
                <Td>
                  {run.emailSubject ? (
                    <FullText text={run.emailSubject}>
                      <CopyableText onClick={() => copy(run.emailSubject as string)}>{run.emailSubject}</CopyableText>
                    </FullText>
                  ) : (
                    <Muted>--</Muted>
                  )}
                </Td>
                <Td>
                  {run.entityLabel ? (
                    <FullText text={run.entityLabel}>
                      {run.entityUrl ? (
                        <EntityLink href={run.entityUrl} target='_blank' rel='noopener noreferrer'>
                          <Clamped>{run.entityLabel}</Clamped>
                        </EntityLink>
                      ) : (
                        <MutedText onClick={() => copy(run.entityLabel as string)}>{run.entityLabel}</MutedText>
                      )}
                    </FullText>
                  ) : (
                    <Muted>--</Muted>
                  )}
                  {referenceMismatch(run.entityLabel, run.entityReference) && <SubText>invoice reads {run.entityReference}</SubText>}
                </Td>
                {/* A pre-alert creates a container and its cargo, so what it extracted IS
                    that cargo - each line a link to the shipment this run created. Every
                    other kind of run has no shipmentLinks and falls back to the one-line
                    summary it always showed.

                    A VIN and a package description get the SAME treatment rather than one
                    each: a 17-character VIN happens to fit and a cargo description happens
                    not to, but that is a fact about the value, not about its kind, and
                    branching on it is what made this column look inconsistent. Clamped to
                    one line, whole value on hover, and the tooltip copies - so a
                    description too long to read is still one click from a search box. */}
                <Td>
                  {run.shipmentLinks.length > 0 ? (
                    <CargoList>
                      {run.shipmentLinks.map((shipment) => (
                        <FullText key={shipment.url} text={shipment.label}>
                          <CargoLink href={shipment.url} target='_blank' rel='noopener noreferrer'>
                            <Clamped>{shipment.label}</Clamped>
                          </CargoLink>
                        </FullText>
                      ))}
                    </CargoList>
                  ) : (
                    run.extractedSummary ?? <Muted>--</Muted>
                  )}
                </Td>
                <Td>{run.confidence ? <StatusChip $tone={confidenceTone(run.confidence)}>{run.confidence}</StatusChip> : <Muted>--</Muted>}</Td>
                <Td>
                  <StatusChip $tone={statusTone(run.processingStatus)}>{statusLabel(run.processingStatus)}</StatusChip>
                  {run.errorReason && (
                    <FullText text={run.errorReason}>
                      <ErrorText onClick={() => copy(run.errorReason as string)}>{run.errorReason}</ErrorText>
                    </FullText>
                  )}
                </Td>
                <Td>{run.createdAt}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      {data.invoiceRunList.paginationMeta && data.invoiceRunList.paginationMeta.totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            shape='rounded'
            count={data.invoiceRunList.paginationMeta.totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
          />
        </PaginationContainer>
      )}
    </>
  )
}

InvoiceRunsTable.Skeleton = () => (
  <TitleContainer>
    <Skeleton height={48} />
    <Skeleton height={48} />
    <Skeleton height={48} />
  </TitleContainer>
)

const query = graphql`
  query InvoiceRunsQuery($status: String, $searchQuery: String, $page: Int, $perPage: Int) {
    invoiceRunList(status: $status, searchQuery: $searchQuery, page: $page, perPage: $perPage) {
      invoiceRuns {
        slug
        source
        fileName
        fileUrl
        emailSubject
        entityLabel
        entityReference
        entityUrl
        extractedSummary
        shipmentLinks {
          label
          url
        }
        confidence
        processingStatus
        errorReason
        createdAt
      }
      totalCount
      paginationMeta {
        totalPages
      }
    }
  }
`

export default InvoiceRuns
